import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import GjsEditor, { AssetsProvider } from '@grapesjs/react';
import grapesjs from 'grapesjs';
import CustomAssetManager from './custom-grapejs/CustomAssetManager';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  Button,
  IconButton,
  TextField,
  Box,
  Stack,
  Typography,
  Skeleton,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  InputAdornment,
  Drawer,
  useMediaQuery,
  Backdrop,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Description as PageIcon,
  ChevronLeft as ChevronLeftIcon,
  DeleteOutline as DeleteIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import './GrapesJSEditor.css';
import { newServer } from 'constants/constants';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#E91E8C',
    },
    secondary: {
      main: '#4A90D9',
    },
  },
});

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#E91E8C',
    },
    secondary: {
      main: '#4A90D9',
    },
  },
});

interface PageConfig {
  id: string;
  name: string;
  html: string;
  css: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// Mapeo de pageId a rutas (basado en App.tsx - ChemLog)
const PAGE_ROUTES: { [key: string]: string } = {
  'page-1': '/',
  'page-2': '/feature/dispatch',
  'page-3': '/feature/invoicing',
  'page-4': '/feature/driver-pay',
  'page-5': '/feature/reporting',
  'page-6': '/implementation',
  'page-7': '/chemlog-demo-video',
  'page-8': '/itollz',
};

// Función para obtener la ruta de una página
const getPageRoute = (pageId: string): string => {
  return PAGE_ROUTES[pageId] || 'No route assigned';
};

export default function PageEditor() {
  const [editor, setEditor] = useState<any>(null);
  const [pages, setPages] = useState<PageConfig[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Loading states
  const [loadingPages, setLoadingPages] = useState(true);
  const [savingPage, setSavingPage] = useState(false);
  const [deletingPage, setDeletingPage] = useState(false);
  const [loadingEditor, setLoadingEditor] = useState(true);
  const [changingPage, setChangingPage] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const isMobile = useMediaQuery('(max-width:900px)');

  // Show notification
  const showNotification = useCallback((message: string, severity: SnackbarState['severity']) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Fetch pages from backend
  const fetchPages = useCallback(async () => {
    setLoadingPages(true);
    try {
      const response = await fetch(`${newServer}landing-page/getAll`);
      if (!response.ok) throw new Error('Failed to fetch pages');
      const data = await response.json();
      setPages(data);
      if (data.length > 0 && !selectedPageId) {
        setSelectedPageId(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      showNotification('Error loading pages. Please try again.', 'error');
    } finally {
      setLoadingPages(false);
    }
  }, [selectedPageId, showNotification]);

  useEffect(() => {
    fetchPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load selected page into editor
  useEffect(() => {
    if (editor && selectedPageId) {
      setChangingPage(true);
      const selectedPage = pages.find((page) => page.id === selectedPageId);
      if (selectedPage) {
        setTimeout(() => {
          editor.setComponents(selectedPage.html);
          editor.setStyle(selectedPage.css);
          setChangingPage(false);
        }, 300);
      }
    }
  }, [selectedPageId, editor, pages]);

  const handleDeleteClick = async (pageId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (pages.length <= 1) {
      showNotification('Cannot delete the last page', 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${pageId}?`)) {
      return;
    }
    
    setDeletingPage(true);
    
    try {
      const response = await fetch(`${newServer}landing-page/${pageId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const newPages = pages.filter((page) => page.id !== pageId);
        setPages(newPages);
        if (selectedPageId === pageId && newPages.length > 0) {
          setSelectedPageId(newPages[0].id);
        }
        showNotification('Page deleted successfully', 'success');
      } else {
        throw new Error('Failed to delete page');
      }
    } catch (error) {
      console.error('Error deleting page:', error);
      showNotification('Error deleting page. Please try again.', 'error');
    } finally {
      setDeletingPage(false);
    }
  };

  const onEditor = (editorInstance: any) => {
    console.log('Editor loaded');
    setEditor(editorInstance);
    setLoadingEditor(false);

    // Agregar bloques adicionales
    const bm = editorInstance.BlockManager;
    
    // Bloques de layout
    bm.add('section', {
      label: 'Section',
      category: 'Layout',
      content: '<section class="py-16 px-4"><div class="max-w-6xl mx-auto">Insert content here</div></section>',
    });
    
    bm.add('container', {
      label: 'Container',
      category: 'Layout', 
      content: '<div class="container mx-auto px-4">Content</div>',
    });

    bm.add('div', {
      label: 'Div Block',
      category: 'Basic',
      content: '<div class="p-4">Div content</div>',
    });

    // Bloques de texto
    bm.add('heading', {
      label: 'Heading',
      category: 'Typography',
      content: '<h2 class="text-3xl font-bold text-gray-800">Heading</h2>',
    });

    bm.add('paragraph', {
      label: 'Paragraph',
      category: 'Typography',
      content: '<p class="text-gray-600 leading-relaxed">Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>',
    });

    bm.add('link-button', {
      label: 'Button',
      category: 'Basic',
      content: '<a href="#" class="inline-block px-6 py-3 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition">Button</a>',
    });

    // Bloques de media
    bm.add('image-block', {
      label: 'Image',
      category: 'Media',
      content: '<img src="https://via.placeholder.com/600x400" class="w-full h-auto rounded-lg" alt="Image"/>',
    });

    bm.add('video-block', {
      label: 'Video',
      category: 'Media',
      content: '<video class="w-full rounded-lg" controls><source src="" type="video/mp4"></video>',
    });

    // Hero section
    bm.add('hero', {
      label: 'Hero Section',
      category: 'Sections',
      content: `
        <section class="relative py-20 px-4 bg-gradient-to-r from-pink-500 to-purple-600">
          <div class="max-w-4xl mx-auto text-center text-white">
            <h1 class="text-5xl font-bold mb-6">Welcome to Our Site</h1>
            <p class="text-xl mb-8 opacity-90">Create amazing experiences with our platform</p>
            <a href="#" class="inline-block px-8 py-4 bg-white text-pink-600 font-bold rounded-full hover:shadow-lg transition">Get Started</a>
          </div>
        </section>
      `,
    });

    // Feature cards
    bm.add('features', {
      label: 'Features Grid',
      category: 'Sections',
      content: `
        <section class="py-16 px-4 bg-gray-50">
          <div class="max-w-6xl mx-auto">
            <h2 class="text-3xl font-bold text-center mb-12">Our Features</h2>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div class="bg-white p-6 rounded-xl shadow-md">
                <div class="w-12 h-12 bg-pink-100 rounded-lg mb-4"></div>
                <h3 class="text-xl font-semibold mb-2">Feature 1</h3>
                <p class="text-gray-600">Description of this amazing feature.</p>
              </div>
              <div class="bg-white p-6 rounded-xl shadow-md">
                <div class="w-12 h-12 bg-blue-100 rounded-lg mb-4"></div>
                <h3 class="text-xl font-semibold mb-2">Feature 2</h3>
                <p class="text-gray-600">Description of this amazing feature.</p>
              </div>
              <div class="bg-white p-6 rounded-xl shadow-md">
                <div class="w-12 h-12 bg-green-100 rounded-lg mb-4"></div>
                <h3 class="text-xl font-semibold mb-2">Feature 3</h3>
                <p class="text-gray-600">Description of this amazing feature.</p>
              </div>
            </div>
          </div>
        </section>
      `,
    });

    // CTA section
    bm.add('cta', {
      label: 'Call to Action',
      category: 'Sections',
      content: `
        <section class="py-16 px-4 bg-gray-900 text-white">
          <div class="max-w-4xl mx-auto text-center">
            <h2 class="text-3xl font-bold mb-4">Ready to get started?</h2>
            <p class="text-gray-400 mb-8">Join thousands of users today.</p>
            <a href="#" class="inline-block px-8 py-3 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600">Sign Up Now</a>
          </div>
        </section>
      `,
    });

    // Footer
    bm.add('footer', {
      label: 'Footer',
      category: 'Sections',
      content: `
        <footer class="py-12 px-4 bg-gray-900 text-gray-400">
          <div class="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 class="text-white font-bold mb-4">Company</h4>
              <ul class="space-y-2">
                <li><a href="#" class="hover:text-white">About</a></li>
                <li><a href="#" class="hover:text-white">Careers</a></li>
                <li><a href="#" class="hover:text-white">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 class="text-white font-bold mb-4">Resources</h4>
              <ul class="space-y-2">
                <li><a href="#" class="hover:text-white">Blog</a></li>
                <li><a href="#" class="hover:text-white">Help Center</a></li>
                <li><a href="#" class="hover:text-white">Guides</a></li>
              </ul>
            </div>
            <div>
              <h4 class="text-white font-bold mb-4">Legal</h4>
              <ul class="space-y-2">
                <li><a href="#" class="hover:text-white">Privacy</a></li>
                <li><a href="#" class="hover:text-white">Terms</a></li>
              </ul>
            </div>
            <div>
              <h4 class="text-white font-bold mb-4">Connect</h4>
              <p class="text-sm">Follow us on social media</p>
            </div>
          </div>
          <div class="max-w-6xl mx-auto mt-8 pt-8 border-t border-gray-800 text-center text-sm">
            © 2024 Company. All rights reserved.
          </div>
        </footer>
      `,
    });

    if (pages.length > 0) {
      const initialPage = pages.find(p => p.id === selectedPageId) || pages[0];
      editorInstance.setComponents(initialPage.html);
      editorInstance.setStyle(initialPage.css);
    }
  };


  const addPage = async () => {
    // Generate unique ID based on highest existing number
    const existingNumbers = pages
      .map(p => {
        const match = p.id.match(/page-(\d+)/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(n => !isNaN(n));
    
    const nextNumber = Math.max(0, ...existingNumbers) + 1;
    
    const newPage: PageConfig = {
      id: `page-${nextNumber}`,
      name: `New Page ${nextNumber}`,
      html: `<div class="min-h-screen bg-gradient-to-br from-pink-50 to-blue-50 flex items-center justify-center">
        <div class="text-center p-8">
          <h1 class="text-5xl font-bold text-gray-800 mb-4">New Page ${nextNumber}</h1>
          <p class="text-xl text-gray-600">Start editing this page</p>
        </div>
      </div>`,
      css: '',
    };
    
    setPages([...pages, newPage]);
    setSelectedPageId(newPage.id);
    showNotification('New page created', 'success');
    
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const saveEdition = async () => {
    if (!editor || !selectedPageId) return;
    
    setSavingPage(true);
    
    try {
      // Solo enviar la página que se editó, no todas
      const currentPage = pages.find(p => p.id === selectedPageId);
      if (!currentPage) throw new Error('Page not found');

      const editedPage = {
        id: selectedPageId,
        name: currentPage.name,
        html: editor.getHtml(),
        css: editor.getCss(),
      };

      // Actualizar estado local
      setPages(prev => prev.map(page =>
        page.id === selectedPageId ? editedPage : page
      ));

      // Solo enviar la página editada al backend
      const response = await fetch(`${newServer}landing-page/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pages: [editedPage] }),
      });

      if (!response.ok) throw new Error('Failed to save');
      
      showNotification('Changes saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving pages:', error);
      showNotification('Error saving changes. Please try again.', 'error');
    } finally {
      setSavingPage(false);
    }
  };

  const handlePageSelect = (pageId: string) => {
    setSelectedPageId(pageId);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  // Mapeo de páginas a carpetas/tags para búsqueda - ChemLog
  const pageTags: Record<string, string[]> = {
    'page-1': ['home', 'landing', 'main'],
    'page-2': ['dispatch', 'feature'],
    'page-3': ['invoicing', 'feature'],
    'page-4': ['driver-pay', 'driver', 'feature'],
    'page-5': ['reporting', 'reports', 'feature'],
    'page-6': ['implementation'],
    'page-7': ['demo', 'video'],
    'page-8': ['itolz', 'toll', 'tollz', 'bridge'],
  };

  // Filter pages based on search (by id, name, or tags)
  const filteredPages = pages.filter(page => {
    const search = searchTerm.toLowerCase();
    const matchesId = page.id.toLowerCase().includes(search);
    const matchesName = page.name.toLowerCase().includes(search);
    const tags = pageTags[page.id] || [];
    const matchesTags = tags.some(tag => tag.toLowerCase().includes(search));
    return matchesId || matchesName || matchesTags;
  });

  // Page list sidebar content - as JSX variable to prevent re-creation on each render
  const sidebarContent = (showCloseButton: boolean, showCollapseButton: boolean = false) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Save Button */}
      <Box sx={{ p: 1, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
          <Typography variant="subtitle2" fontWeight={700} color="#1e293b" fontSize="0.8rem">
            Pages
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton 
                size="small"
                onClick={fetchPages} 
                disabled={loadingPages}
                sx={{ 
                  p: 0.5,
                  color: '#64748b',
                  '&:hover': { color: '#E91E8C' }
                }}
              >
                <RefreshIcon sx={{ 
                  fontSize: 16,
                  animation: loadingPages ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }} />
              </IconButton>
            </Tooltip>
            {showCloseButton && (
              <IconButton onClick={() => setMobileDrawerOpen(false)} size="small" sx={{ p: 0.5 }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            {showCollapseButton && (
              <Tooltip title="Collapse sidebar">
                <IconButton 
                  onClick={() => setSidebarOpen(false)} 
                  size="small" 
                  sx={{ 
                    p: 0.5,
                    color: '#64748b',
                    '&:hover': { color: '#E91E8C' }
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        
        {/* Search */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8', fontSize: 14 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f8fafc',
              borderRadius: 1,
              fontSize: '0.75rem',
              '& input': { py: 0.5, fontSize: '0.75rem' },
              '& fieldset': { borderColor: '#e2e8f0' },
              '&:hover fieldset': { borderColor: '#cbd5e1' },
              '&.Mui-focused fieldset': { borderColor: '#E91E8C' },
            },
          }}
        />
      </Box>

      {/* Action Buttons Row */}
      <Box sx={{ p: 1, pt: 0.75, display: 'flex', gap: 0.5 }}>
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={addPage}
          sx={{
            flex: 1,
            bgcolor: '#E91E8C',
            color: 'white',
            borderRadius: 1,
            py: 0.5,
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'none',
            minWidth: 0,
            '&:hover': { bgcolor: '#C4177A' }
          }}
        >
          Add
        </Button>
        <Button
          size="small"
          startIcon={savingPage ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: 14 }} />}
          onClick={saveEdition}
          disabled={savingPage || !selectedPageId}
          sx={{
            flex: 1,
            bgcolor: '#22c55e',
            color: 'white',
            borderRadius: 1,
            py: 0.5,
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'none',
            minWidth: 0,
            '&:hover': { bgcolor: '#16a34a' },
            '&:disabled': { bgcolor: '#86efac', color: 'white' }
          }}
        >
          {savingPage ? '...' : 'Save'}
        </Button>
      </Box>

      {/* Pages List - with scroll */}
      <Box sx={{ 
        maxHeight: 'calc(100vh - 180px)', 
        overflowY: 'auto', 
        overflowX: 'hidden',
        p: 1.5, 
        pt: 0 
      }}>
        {loadingPages ? (
          <Stack spacing={0.5}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton 
                key={i} 
                variant="rounded" 
                height={40} 
                sx={{ borderRadius: 1.5 }}
              />
            ))}
          </Stack>
        ) : filteredPages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <PageIcon sx={{ fontSize: 32, color: '#cbd5e1', mb: 0.5 }} />
            <Typography color="text.secondary" fontSize="0.75rem">
              {searchTerm ? 'No pages found' : 'No pages yet'}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0.25}>
            {filteredPages.map((page) => (
              <Box
                key={page.id}
                onClick={() => handlePageSelect(page.id)}
                sx={{
                  py: 0.5,
                  px: 1,
                  cursor: 'pointer',
                  borderRadius: 1,
                  borderLeft: '3px solid',
                  borderLeftColor: selectedPageId === page.id ? '#E91E8C' : 'transparent',
                  bgcolor: selectedPageId === page.id ? '#fdf2f8' : 'transparent',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  '&:hover': { 
                    bgcolor: selectedPageId === page.id ? '#fdf2f8' : '#f8fafc',
                  },
                  '&:hover .action-btn': {
                    opacity: 1,
                  },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      color: selectedPageId === page.id ? '#E91E8C' : '#1e293b',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {page.id}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.6rem',
                      lineHeight: 1.2,
                      fontFamily: 'monospace',
                    }}
                  >
                    {getPageRoute(page.id)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                  <Tooltip title="View page" placement="top">
                    <IconButton
                      className="action-btn"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const route = getPageRoute(page.id);
                        if (route !== 'No route assigned') {
                          window.open(route, '_blank');
                        }
                      }}
                      disabled={getPageRoute(page.id) === 'No route assigned'}
                      sx={{
                        opacity: 0,
                        p: 0.25,
                        color: '#94a3b8',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          color: '#3b82f6',
                          bgcolor: 'rgba(59, 130, 246, 0.1)',
                        },
                        '&:disabled': {
                          opacity: 0,
                        },
                      }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete page" placement="top">
                    <IconButton
                      className="action-btn"
                      size="small"
                      onClick={(e) => handleDeleteClick(page.id, e)}
                      sx={{
                        opacity: 0,
                        p: 0.25,
                        color: '#94a3b8',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          color: '#ef4444',
                          bgcolor: 'rgba(239, 68, 68, 0.1)',
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Footer info */}
      <Box sx={{ p: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        <Chip 
          size="small" 
          label={`${pages.length} page${pages.length !== 1 ? 's' : ''}`}
          sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontSize: '0.7rem', height: 22 }}
        />
      </Box>
    </Box>
  );

  // Memoized sidebar for desktop (with collapse button)
  const desktopSidebar = sidebarContent(false, true);
  
  // Memoized sidebar for mobile (with close button, no collapse)
  const mobileSidebar = sidebarContent(true, false);

  // Loading inicial - mostrar un único loader mientras carga todo
  const isInitialLoading = loadingPages && loadingEditor;

  if (isInitialLoading) {
    return (
      <ThemeProvider theme={lightTheme}>
        <Box
          sx={{
            bgcolor: '#f8fafc',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: '#E91E8C' }} size={48} />
          <Typography color="#64748b" fontSize="0.9rem">
            Loading editor...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <Box sx={{ 
        bgcolor: '#f8fafc', 
        height: '100%',
        display: 'flex',
        overflow: 'hidden',
      }}>
        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
          {/* Desktop Sidebar - Collapsible */}
          {!isMobile && (
            <>
              <Box
                sx={{
                  width: sidebarOpen ? 200 : 0,
                  flexShrink: 0,
                  bgcolor: '#fff',
                  borderRight: sidebarOpen ? '1px solid #e2e8f0' : 'none',
                  height: '100%',
                  overflow: 'hidden',
                  transition: 'width 0.3s ease',
                }}
              >
                {sidebarOpen && desktopSidebar}
              </Box>
              
              {/* Toggle Button - Only visible when sidebar is closed */}
              {!sidebarOpen && (
                <Tooltip title="Expand sidebar" placement="right">
                  <IconButton
                    onClick={() => setSidebarOpen(true)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      bgcolor: '#1e293b',
                      color: '#fff',
                      width: 28,
                      height: 28,
                      zIndex: 8,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      '&:hover': {
                        bgcolor: '#E91E8C',
                      },
                    }}
                  >
                    <ChevronLeftIcon 
                      sx={{ 
                        fontSize: 18,
                        transform: 'rotate(180deg)',
                      }} 
                    />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}

          {/* Mobile Drawer */}
          <Drawer
            anchor="left"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            sx={{
              '& .MuiDrawer-paper': {
                width: '85%',
                maxWidth: 320,
              },
            }}
          >
            {mobileSidebar}
          </Drawer>

          {/* Editor Area */}
          <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            {/* Loading/Saving overlay */}
            <Backdrop
              open={changingPage || loadingEditor || savingPage}
              sx={{
                position: 'absolute',
                zIndex: 100,
                bgcolor: 'rgba(255,255,255,0.95)',
                flexDirection: 'column',
              }}
            >
              <CircularProgress sx={{ color: savingPage ? '#22c55e' : '#E91E8C', mb: 2 }} />
              <Typography color="#64748b">
                {loadingEditor ? 'Loading editor...' : savingPage ? 'Saving changes...' : 'Loading page...'}
              </Typography>
            </Backdrop>

            {/* Deleting overlay */}
            <Backdrop
              open={deletingPage}
              sx={{
                position: 'absolute',
                zIndex: 100,
                bgcolor: 'rgba(255,255,255,0.95)',
                flexDirection: 'column',
              }}
            >
              <CircularProgress sx={{ color: '#ef4444', mb: 2 }} />
              <Typography color="#64748b">Deleting page...</Typography>
            </Backdrop>

            {/* GrapesJS Editor */}
            <Box sx={{ height: '100%', bgcolor: '#f8fafc' }}>
              <ThemeProvider theme={theme}>
                <GjsEditor
                  grapesjs={grapesjs}
                  plugins={[
                    {
                      id: 'gjs-blocks-basic',
                      src: 'https://unpkg.com/grapesjs-blocks-basic',
                    },
                    {
                      id: 'grapesjs-custom-code',
                      src: 'https://unpkg.com/grapesjs-custom-code',
                    },
                    {
                      id: 'grapesjs-plugin-forms',
                      src: 'https://unpkg.com/grapesjs-plugin-forms',
                    },
                    {
                      id: 'grapesjs-tailwind',
                      src: 'https://unpkg.com/grapesjs-tailwind',
                    },
                  ]}
                  grapesjsCss="https://unpkg.com/grapesjs/dist/css/grapes.min.css"
                  options={{
                    container: '#gjs',
                    fromElement: true,
                    height: '100%',
                    width: '100%',
                    storageManager: false,
                    assetManager: {
                      embedAsBase64: false, // Cambiado a false - las imágenes se suben a S3
                      custom: true,
                    },
                    deviceManager: {
                      devices: [
                        { name: 'Desktop', width: '' },
                        { name: 'Tablet', width: '768px', widthMedia: '992px' },
                        { name: 'Mobile', width: '375px', widthMedia: '480px' },
                      ]
                    },
                    canvas: {
                      styles: [
                        'https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css',
                        'https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&family=Nunito:wght@400;600;700;800&display=swap',
                      ],
                    },
                    styleManager: {
                      sectors: [
                        {
                          name: 'General',
                          open: true,
                          properties: ['display', 'float', 'position', 'top', 'right', 'left', 'bottom'],
                        },
                        {
                          name: 'Dimension',
                          open: false,
                          properties: ['width', 'height', 'max-width', 'min-height', 'margin', 'padding'],
                        },
                        {
                          name: 'Typography',
                          open: false,
                          properties: ['font-family', 'font-size', 'font-weight', 'letter-spacing', 'color', 'line-height', 'text-align', 'text-decoration'],
                        },
                        {
                          name: 'Decorations',
                          open: false,
                          properties: ['background-color', 'border-radius', 'border', 'box-shadow', 'opacity'],
                        },
                      ],
                    },
                  }}
                  onEditor={onEditor}
                >
                  <AssetsProvider>
                    {(props) => <CustomAssetManager {...props} currentPageId={selectedPageId || undefined} />}
                  </AssetsProvider>
                </GjsEditor>
              </ThemeProvider>
            </Box>
          </Box>
        </Box>

        {/* Snackbar notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{ 
              borderRadius: 2,
              fontWeight: 500,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
